import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import { useRealtimePing } from '@/hooks/useRealtimeTopic';
import { createClient } from '@supabase/supabase-js';
import { notifyLandlordTicketResponse } from '@/lib/notifications';
import type {
  SupportTicket,
  TicketMessage,
  TicketCategory,
  TicketPriority,
  TicketStatus,
  CreateTicketInput,
  CreateLoginTicketInput,
} from '@/types/database';

// Query keys
export const supportKeys = {
  all: ['support'] as const,
  myTickets: (landlordId: string) => [...supportKeys.all, 'my', landlordId] as const,
  allTickets: () => [...supportKeys.all, 'admin'] as const,
  ticket: (ticketId: string) => [...supportKeys.all, 'ticket', ticketId] as const,
  messages: (ticketId: string) => [...supportKeys.all, 'messages', ticketId] as const,
  unreadCount: (landlordId: string) => [...supportKeys.all, 'unread', landlordId] as const,
  adminUnreadCount: () => [...supportKeys.all, 'admin-unread'] as const,
};

// ============================================
// LANDLORD HOOKS
// ============================================

/**
 * Landlord's own tickets (via RPC)
 */
export function useMyTickets() {
  const { user } = useUser();
  const landlordId = user?.id;

  return useQuery({
    queryKey: supportKeys.myTickets(landlordId || ''),
    queryFn: async () => {
      if (!landlordId) return [];

      const { data, error } = await getSupabase()
        .rpc('get_my_support_tickets');

      if (error) {
        console.error('Error fetching my tickets:', error);
        throw error;
      }

      return (data || []) as SupportTicket[];
    },
    enabled: !!landlordId,
    refetchInterval: 30000,
  });
}

/**
 * Messages for a specific ticket (landlord - own tickets only)
 */
export function useTicketMessages(ticketId: string | undefined) {
  return useQuery({
    queryKey: supportKeys.messages(ticketId || ''),
    queryFn: async () => {
      if (!ticketId) return [];

      const { data, error } = await getSupabase()
        .rpc('get_my_ticket_messages', { p_ticket_id: ticketId });

      if (error) {
        console.error('Error fetching ticket messages:', error);
        throw error;
      }

      return (data || []) as TicketMessage[];
    },
    enabled: !!ticketId,
  });
}

/**
 * Unread ticket message count for landlord (admin responses they haven't read)
 */
export function useUnreadTicketCount() {
  const { user } = useUser();
  const landlordId = user?.id;

  return useQuery({
    queryKey: supportKeys.unreadCount(landlordId || ''),
    queryFn: async () => {
      if (!landlordId) return 0;

      const { data, error } = await getSupabase()
        .rpc('get_unread_ticket_messages_count', { p_landlord_id: landlordId });

      if (error) {
        console.error('Error fetching unread ticket count:', error);
        return 0;
      }

      return (data as number) || 0;
    },
    enabled: !!landlordId,
    refetchInterval: 15000,
  });
}

/**
 * Create a new support ticket (authenticated landlord via RPC)
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (input: CreateTicketInput) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await getSupabase()
        .rpc('create_my_support_ticket', {
          p_category: input.category,
          p_subject: input.subject,
          p_description: input.description,
          p_priority: input.priority || 'medium',
          p_screenshot_urls: input.screenshot_urls || [],
          p_ai_conversation_summary: input.ai_conversation_summary || null,
          p_page_url: input.page_url || null,
          p_browser_info: input.browser_info || null,
        });

      if (error) {
        console.error('Error creating ticket:', error);
        throw error;
      }

      return data as SupportTicket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

/**
 * Send a message on a ticket (landlord replying via RPC)
 */
export function useSendTicketMessage() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({
      ticketId,
      content,
      attachments,
    }: {
      ticketId: string;
      content: string;
      attachments?: string[];
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await getSupabase()
        .rpc('send_my_ticket_message', {
          p_ticket_id: ticketId,
          p_content: content.trim(),
          p_attachments: attachments || [],
        });

      if (error) {
        console.error('Error sending ticket message:', error);
        throw error;
      }

      return data as TicketMessage;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.messages(data.ticket_id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

/**
 * Mark admin messages as read on a ticket (via RPC)
 */
export function useMarkTicketMessagesRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ticketId: string) => {
      const { error } = await getSupabase()
        .rpc('mark_my_ticket_messages_read', { p_ticket_id: ticketId });

      if (error) {
        console.error('Error marking ticket messages as read:', error);
        throw error;
      }

      return ticketId;
    },
    onSuccess: (ticketId) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

// ============================================
// ADMIN HOOKS
// ============================================

export interface TicketFilters {
  status?: TicketStatus;
  category?: TicketCategory;
  priority?: TicketPriority;
  search?: string;
}

/**
 * All tickets (admin only via RPC)
 */
export function useAllTickets(filters?: TicketFilters) {
  return useQuery({
    queryKey: [...supportKeys.allTickets(), filters || {}],
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .rpc('admin_get_support_tickets', {
          p_status: filters?.status || null,
          p_category: filters?.category || null,
          p_priority: filters?.priority || null,
          p_search: filters?.search || null,
        });

      if (error) {
        console.error('Error fetching all tickets:', error);
        throw error;
      }

      return (data || []) as SupportTicket[];
    },
    refetchInterval: 15000,
  });
}

/**
 * Open tickets count (for admin badge)
 */
export function useAdminUnreadTicketCount() {
  return useQuery({
    queryKey: supportKeys.adminUnreadCount(),
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .rpc('get_open_tickets_count');

      if (error) {
        console.error('Error fetching open tickets count:', error);
        return 0;
      }

      return (data as number) || 0;
    },
    refetchInterval: 15000,
  });
}

/**
 * Admin ticket messages (via RPC)
 */
export function useAdminTicketMessages(ticketId: string | undefined) {
  return useQuery({
    queryKey: [...supportKeys.messages(ticketId || ''), 'admin'],
    queryFn: async () => {
      if (!ticketId) return [];

      const { data, error } = await getSupabase()
        .rpc('admin_get_ticket_messages', { p_ticket_id: ticketId });

      if (error) {
        console.error('Error fetching admin ticket messages:', error);
        throw error;
      }

      return (data || []) as TicketMessage[];
    },
    enabled: !!ticketId,
  });
}

/**
 * Admin send message on a ticket (via RPC)
 */
export function useAdminSendTicketMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketId,
      content,
      attachments,
    }: {
      ticketId: string;
      content: string;
      attachments?: string[];
    }) => {
      const { data, error } = await getSupabase()
        .rpc('admin_send_ticket_message', {
          p_ticket_id: ticketId,
          p_content: content.trim(),
          p_attachments: attachments || [],
        });

      if (error) {
        console.error('Error sending admin ticket message:', error);
        throw error;
      }

      return data as TicketMessage;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.messages(data.ticket_id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.all });

      // Send push notification to landlord
      try {
        // Use RPC to get ticket info since direct table access may be blocked
        const tickets = await getSupabase()
          .rpc('admin_get_support_tickets', {
            p_status: null, p_category: null, p_priority: null, p_search: null,
          });

        const ticket = (tickets.data as SupportTicket[] || []).find(t => t.id === data.ticket_id);

        if (ticket?.landlord_id) {
          notifyLandlordTicketResponse({
            landlordId: ticket.landlord_id,
            ticketId: data.ticket_id,
            messagePreview: data.content.slice(0, 100),
          });
        }
      } catch {
        // Non-critical - notification failure shouldn't break the flow
      }
    },
  });
}

/**
 * Update ticket status/priority (admin via RPC)
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketId,
      updates,
    }: {
      ticketId: string;
      updates: Partial<Pick<SupportTicket, 'status' | 'priority'>>;
    }) => {
      const { data, error } = await getSupabase()
        .rpc('admin_update_support_ticket', {
          p_ticket_id: ticketId,
          p_status: updates.status || null,
          p_priority: updates.priority || null,
        });

      if (error) {
        console.error('Error updating ticket:', error);
        throw error;
      }

      return data as SupportTicket;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(data.id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

// ============================================
// LOGIN PAGE TICKET (ANONYMOUS)
// ============================================

/**
 * Create ticket from login page (no auth required)
 * Uses anon Supabase key
 */
export function useCreateLoginTicket() {
  return useMutation({
    mutationFn: async (input: CreateLoginTicketInput) => {
      // Use anon client directly (no Clerk auth)
      const anonClient = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY
      );

      // Use RPC function (SECURITY DEFINER) to bypass table-level RLS
      const { data, error } = await anonClient
        .rpc('create_login_support_ticket', {
          p_email: input.email,
          p_category: input.category || 'other',
          p_subject: input.subject,
          p_description: input.description,
          p_screenshot_urls: input.screenshot_urls || [],
          p_browser_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
          },
        });

      if (error) {
        console.error('Error creating login ticket:', error);
        throw error;
      }

      // RPC returns ticket ID
      return { id: data } as SupportTicket;
    },
  });
}

// ============================================
// HELPERS
// ============================================

/**
 * Capture current browser context for ticket auto-fill
 */
export function captureBrowserContext() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
  };
}

/**
 * Format time ago for ticket display
 */
export function formatTicketTime(dateStr: string, locale: string = 'hr-HR'): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return locale === 'hr-HR' ? 'Upravo sada' : 'Just now';
  if (diffMins < 60) return locale === 'hr-HR' ? `Prije ${diffMins} min` : `${diffMins} min ago`;
  if (diffHours < 24) return locale === 'hr-HR' ? `Prije ${diffHours}h` : `${diffHours}h ago`;
  if (diffDays < 7) return locale === 'hr-HR' ? `Prije ${diffDays}d` : `${diffDays}d ago`;
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/**
 * Live ticket thread: the database pings the ticket's private topic on new or
 * changed messages; the thread (host or admin view) is re-read. `onChange`
 * runs after each ping (e.g. mark the admin's new reply as read).
 */
export function useTicketRealtime(ticketId: string | undefined, onChange?: () => void) {
  const queryClient = useQueryClient();
  useRealtimePing('ticket', ticketId, () => {
    queryClient.invalidateQueries({ queryKey: supportKeys.all });
    onChange?.();
  });
}
